/**
 * InteriorMarkerSyncModule — ALWAYS-ON: turns interior_marker rows into
 * ENTERABLE buildings for every visitor. An owner flags a freeform part or an
 * admin placement in Edit Mode; here the object's rendered bbox becomes an
 * EnterableBuilding (the procedural shell + door + colliders come from the
 * existing interior lib), registered under `mark:<kind>:<id>` so the door
 * markers, E-to-enter, clamping and furnishing paths all light up unchanged.
 * Meshes load asynchronously, so registration retries at 1 Hz until each
 * marked object has a mesh; deleted markers unregister (exiting first).
 */
import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { makeSystem, Phase } from '../engine-compat.js';
import { enterables, interiorEligibleMarker, type EnterableBuilding } from '../lib/building_interiors.js';
import { orientedBox, objectCorners } from '../lib/interior_capability.js';
import type { FreeformBuildModule } from './FreeformBuildModule.js';
import type { AtlasAdminEditorModule } from './AtlasAdminEditorModule.js';

export class InteriorMarkerSyncModule implements IModule {
  readonly name = 'InteriorMarkerSync';

  private sub: { unsubscribe(): void } | null = null;
  private subWorld: bigint | null = null;
  private acc = 0;
  private bound = false;

  constructor(private readonly freeform: FreeformBuildModule, private readonly admin: AtlasAdminEditorModule) {}

  init(app: App): void {
    bus.on('atlas:world-entered', () => this.onWorld());
    bus.on('atlas:world-cleared', () => this.unregisterAll());
    bus.on('atlas:account-ready', () => this.bind());
    app.world.addSystem(makeSystem('InteriorMarkers', Phase.Animation, 41, (dt) => { this.acc += dt; if (this.acc >= 1) { this.acc = 0; this.sync(); } }));
  }

  private bind(): void {
    if (this.bound) return;
    this.bound = true;
    this.onWorld();
  }

  private onWorld(): void {
    const conn = atlas.conn;
    const wid = atlas.currentWorldId;
    if (!conn || wid === null || this.subWorld === wid) return;
    const prev = this.sub;
    this.subWorld = wid;
    this.unregisterAll();
    try {
      this.sub = conn.subscriptionBuilder()
        .onApplied(() => { if (prev) prev.unsubscribe(); this.sync(); })
        .subscribe([`SELECT * FROM interior_marker WHERE world_id = ${wid}`]);
    } catch { /* pre-publish server */ }
  }

  /** Register every marked object that has a mesh; drop stale registrations. */
  private sync(): void {
    const conn = atlas.conn;
    const wid = atlas.currentWorldId;
    if (!conn || wid === null) return;
    const wanted = new Set<string>();
    try {
      for (const m of conn.db.interiorMarker.iter()) {
        if (m.worldId !== wid) continue;
        const id = `mark:${m.refKind}:${m.refId.toString()}`;
        wanted.add(id);
        if (enterables.has(id)) continue;
        const ent = this.build(id, m.refKind, m.refId.toString());
        if (ent) enterables.set(id, ent);
      }
    } catch { return; }
    for (const id of [...enterables.keys()]) {
      if (id.startsWith('mark:') && !wanted.has(id)) enterables.delete(id);
    }
  }

  private build(id: string, refKind: string, refId: string): EnterableBuilding | null {
    let mesh: THREE.Object3D | null = null;
    let hide: () => void = () => {};
    let restore: () => void = () => {};
    if (refKind === 'part') {
      mesh = this.freeform.partMesh(refId) ?? null;
      hide = () => this.freeform.setPartHidden(refId, true);
      restore = () => this.freeform.setPartHidden(refId, false);
    } else if (refKind === 'placement') {
      mesh = this.admin.placementMesh(refId);
      hide = () => this.admin.setPlacementHidden(refId, true);
      restore = () => this.admin.setPlacementHidden(refId, false);
    }
    let box: ReturnType<typeof orientedBox> = null, yaw = 0;
    if (mesh) {
      mesh.updateWorldMatrix(true, true);
      yaw = new THREE.Euler().setFromQuaternion(mesh.getWorldQuaternion(new THREE.Quaternion()), 'YXZ').y;
      // THE MARKER'S BOX IS MEASURED IN ITS OWN YAW FRAME (2026-10-01): this took the WORLD box (`setFromObject`) and
      // then turned the shell by the object's yaw AGAIN — a 10 x 4 m part at 90° got a 4 x 10 shell lying across it, and
      // at 45° a 9.9 x 9.9 one. At yaw 0 the two are the same box (verify:interior-coverage pins both).
      box = orientedBox(objectCorners(mesh), yaw);
    } else if (refKind === 'placement') {
      // AN INSTANCED PLACEMENT REGISTERS FROM ITS ROW (slice C, 2026-10-01 — slice A's item 5): in play mode a placement
      // is instanced and has no mesh, so a marker over one built only while an editor session held the individual
      // representation — ordinary visitors never saw the door. The box is the row's transform over the bbox the
      // instancer measured for its prefab; hide / restore go through the instancer (setPlacementHidden).
      const pc = this.admin.placementCorners(refId);
      if (!pc) return null; // not instanced here yet (out of the draw distance, or still loading) — asked again next second
      yaw = pc.yaw;
      box = orientedBox(pc.corners, yaw);
    } else return null;
    if (!box || !interiorEligibleMarker(box.w, box.h, box.d)) return null; // smaller than the marker floor has no room for a shell
    return {
      id,
      name: refKind === 'part' ? `structure #${refId}` : `placement #${refId}`,
      key: `mark:${refKind}`,
      x: box.x, z: box.z, yaw, groundY: box.groundY,
      w: box.w, h: box.h, d: box.d,
      hide, restore,
    };
  }

  private unregisterAll(): void {
    for (const id of [...enterables.keys()]) if (id.startsWith('mark:')) enterables.delete(id);
  }

  dispose(): void {
    this.sub?.unsubscribe();
    this.unregisterAll();
  }
}
